import * as React from "react";
import { Box, Card, Chip, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { CheckIcon, PaperPlaneRightIcon, SparkleIcon } from "@phosphor-icons/react";

import type { LeadMessage } from "@/hooks/api/use-lead-finder";

import { tint } from "./tint";

/** An answer takes a model call or three plus ZoomInfo searches; say what is happening meanwhile. */
const WORKING = ["Reading your message", "Working out who buys this", "Searching ZoomInfo", "Checking the results fit"];

/** A chip whose words wrap onto more lines instead of being cut off: answers and suggestions are sentences. */
const wraps = {
	height: "auto",
	maxWidth: "100%",
	"& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: 0.75, lineHeight: 1.4 },
};

/** The lead finder's mark beside each of its lines. */
function FinderMark(): React.JSX.Element {
	return (
		<Box
			aria-hidden
			sx={[
				{
					width: 28,
					height: 28,
					flexShrink: 0,
					borderRadius: "8px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				},
				tint("primary"),
			]}
		>
			<SparkleIcon size={15} weight="fill" />
		</Box>
	);
}

function Thinking(): React.JSX.Element {
	const [step, setStep] = React.useState(0);
	React.useEffect(() => {
		const timer = setInterval(() => setStep((current) => Math.min(current + 1, WORKING.length - 1)), 3500);
		return () => clearInterval(timer);
	}, []);
	return (
		<Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
			<FinderMark />
			<CircularProgress size={14} />
			<Typography variant="body2" color="text.secondary">
				{WORKING[step]}...
			</Typography>
		</Stack>
	);
}

/** A finder line that came with a search: "1,925 contacts fit." first, then what it changed and why. */
function Found({ text }: { text: string }): React.JSX.Element {
	// Only the count is split off: the notes are the model's sentences, and "e.g." or "U.S." is not a sentence end.
	const cut = text.indexOf(". ");
	const headline = cut === -1 ? text : text.slice(0, cut + 1);
	const notes = cut === -1 ? [] : [text.slice(cut + 2)];
	return (
		<Box sx={{ border: "1px solid var(--mui-palette-divider)", borderRadius: "12px", px: 1.75, py: 1.5 }}>
			<Typography variant="subtitle2">{headline}</Typography>
			{notes.length > 0 && (
				<Stack component="ul" spacing={0.75} sx={{ listStyle: "none", m: 0, mt: 1, p: 0 }}>
					{notes.map((note) => (
						<Stack component="li" key={note} direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
							<Box component="span" sx={{ color: "success.main", mt: "3px", flexShrink: 0, display: "flex" }}>
								<CheckIcon size={13} weight="bold" />
							</Box>
							<Typography variant="body2" color="text.secondary">
								{note}
							</Typography>
						</Stack>
					))}
				</Stack>
			)}
		</Box>
	);
}

interface LineProps {
	message: LeadMessage;
	/** The customer's answer to this line's question, when they gave one. */
	answer?: string;
	/** Tap-to-answer is open: this is the last line, and nothing is running. */
	open: boolean;
	onAnswer: (option: string) => void;
}

function Line({ message, answer, open, onAnswer }: LineProps): React.JSX.Element {
	if (message.role === "customer")
		return (
			<Box
				sx={(theme: any) => ({
					alignSelf: "flex-end",
					maxWidth: "85%",
					px: 1.75,
					py: 1.25,
					borderRadius: "14px 14px 4px 14px",
					// White on the brand violet in both schemes: dark mode's lighter primary would leave white text at 3:1.
					backgroundColor: "var(--mui-palette-primary-500)",
					color: "#ffffff",
					...theme.applyStyles("dark", { backgroundColor: "var(--mui-palette-primary-600)" }),
				})}
			>
				<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "inherit" }}>
					{message.text}
				</Typography>
			</Box>
		);
	return (
		<Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
			<FinderMark />
			<Stack spacing={1} sx={{ flex: "1 1 auto", minWidth: 0 }}>
				{message.total === undefined ? (
					<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", pt: 0.5 }}>
						{message.text}
					</Typography>
				) : (
					<Found text={message.text} />
				)}
				{message.options && (open || answer) && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
						{message.options.map((option) =>
							open ? (
								<Chip
									key={option}
									label={option}
									color="primary"
									variant="outlined"
									onClick={() => onAnswer(option)}
									sx={wraps}
								/>
							) : (
								<Chip
									key={option}
									label={option}
									size="small"
									variant={option === answer ? "filled" : "outlined"}
									color={option === answer ? "primary" : "default"}
									icon={option === answer ? <CheckIcon size={12} weight="bold" /> : undefined}
									sx={option === answer ? wraps : { ...wraps, color: "text.secondary" }}
								/>
							)
						)}
					</Box>
				)}
				{message.suggestions && open && (
					<Stack spacing={0.75}>
						<Typography variant="caption" color="text.secondary">
							Try next
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
							{message.suggestions.map((suggestion) => (
								<Chip
									key={suggestion}
									label={suggestion}
									size="small"
									variant="outlined"
									onClick={() => onAnswer(suggestion)}
									sx={{ ...wraps, borderStyle: "dashed", textAlign: "left" }}
								/>
							))}
						</Box>
					</Stack>
				)}
			</Stack>
		</Stack>
	);
}

export interface LeadChatProps {
	messages: LeadMessage[];
	/** The customer's line on its way, shown until the finder answers. */
	pending: string | null;
	thinking: boolean;
	/** Something else changes the search, e.g. a removed filter: a line now would be answered from the old one. */
	busy: boolean;
	disabled: boolean;
	/** Resolves true once the finder has answered; the line is put back to send again otherwise. */
	onSend: (text: string) => Promise<boolean>;
}

/** The conversation with the lead finder: it asks only what changes who to call, then searches. */
export function LeadChat({ messages, pending, thinking, busy, disabled, onSend }: LeadChatProps): React.JSX.Element {
	const [text, setText] = React.useState("");
	const list = React.useRef<HTMLDivElement>(null);
	const shown = pending ? [...messages, { role: "customer" as const, text: pending }] : messages;
	const closed = thinking || busy || disabled;

	React.useEffect(() => {
		// The list scrolls, not the page: the newest line shows without the window jumping past the header.
		if (list.current) list.current.scrollTop = list.current.scrollHeight;
	}, [shown.length, thinking]);

	const send = async (value: string) => {
		const line = value.trim();
		if (!line || closed) return;
		setText("");
		if (!(await onSend(line))) setText(line);
	};

	return (
		<Card
			sx={{
				display: "flex",
				flexDirection: "column",
				height: { xs: "min(70vh, 560px)", md: "calc(100vh - 220px)" },
				minHeight: 460,
				borderRadius: "16px",
			}}
		>
			<Stack
				ref={list}
				spacing={2}
				role="log"
				aria-live="polite"
				aria-label="Conversation with the lead finder"
				sx={{ flex: 1, overflowY: "auto", p: 2.5 }}
			>
				{shown.map((message, index) => (
					<Line
						key={index}
						message={message}
						answer={shown[index + 1]?.role === "customer" ? shown[index + 1].text : undefined}
						open={index === shown.length - 1 && !closed}
						onAnswer={(option) => void send(option)}
					/>
				))}
				{thinking && <Thinking />}
			</Stack>
			<Stack
				component="form"
				direction="row"
				spacing={1}
				sx={{ p: 1.5, borderTop: "1px solid var(--mui-palette-divider)", alignItems: "flex-end" }}
				onSubmit={(event: React.FormEvent) => {
					event.preventDefault();
					void send(text);
				}}
			>
				<TextField
					fullWidth
					multiline
					maxRows={4}
					placeholder={disabled ? "Leads are on their way" : "Ask for a change, e.g. only clinics in the CBD"}
					value={text}
					onChange={(event) => setText(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter" && !event.shiftKey) {
							event.preventDefault();
							void send(text);
						}
					}}
					disabled={disabled}
					inputProps={{ maxLength: 1000, "aria-label": "Message the lead finder" }}
					sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
				/>
				<IconButton
					type="submit"
					disabled={closed || !text.trim()}
					aria-label="Send"
					sx={{
						width: 44,
						height: 44,
						borderRadius: "12px",
						bgcolor: "primary.main",
						color: "primary.contrastText",
						"&:hover": { bgcolor: "primary.dark" },
						"&.Mui-disabled": { bgcolor: "action.disabledBackground" },
					}}
				>
					<PaperPlaneRightIcon size={18} />
				</IconButton>
			</Stack>
		</Card>
	);
}
